"use client"
import { createContext, useCallback, useContext, useRef, useState } from "react"

type ToastType = "error" | "success" | "info"
interface ToastItem {
  id: number
  message: string
  type: ToastType
}

const DURATION_MS = 5000

const ToastContext = createContext<(message: string, type?: ToastType) => void>(() => {})

export function useToast() {
  return useContext(ToastContext)
}

const STYLES: Record<ToastType, string> = {
  error: "border-red-800 bg-red-950 text-red-300",
  success: "border-green-800 bg-green-950 text-green-300",
  info: "border-gray-700 bg-gray-900 text-gray-300",
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  const idRef = useRef(0)

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    clearTimeout(timers.current[id])
    delete timers.current[id]
  }, [])

  const show = useCallback((message: string, type: ToastType = "error") => {
    const id = ++idRef.current
    setToasts((prev) => [...prev, { id, message, type }])
    timers.current[id] = setTimeout(() => remove(id), DURATION_MS)
  }, [remove])

  const pause = (id: number) => clearTimeout(timers.current[id])
  const resume = (id: number) => {
    timers.current[id] = setTimeout(() => remove(id), DURATION_MS)
  }

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            onMouseEnter={() => pause(t.id)}
            onMouseLeave={() => resume(t.id)}
            className={`flex max-w-sm items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${STYLES[t.type]}`}
          >
            <span className="flex-1">{t.message}</span>
            <button
              onClick={() => remove(t.id)}
              className="shrink-0 text-current opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
